// @givmo/board — the meeting list: what is coming up, and what has happened.
//
// Scheduling is Member+Owner work (`board_meeting:write`) — it is the recording officer's day-to-day
// job and none of it is the organization's RECORD. The minutes are, and those go through the record core's
// Owner-only approval.
//
// ══ ONE QUIET FACT PER MEETING (CBG-UX1, Jonathan's approved mockup m1) ═══════════════════════
//
// The meetings are rows in one card per group, each saying one thing beside its title and time: the
// notice arithmetic as a single line ("Scheduled 21 days ahead · your notice period is 10 days
// (Art. III §4)"), or "Cancelled · <reason>". No "Regular" or "Scheduled" tag — they restate the
// default. The line is `noticeFact` (meetings-decode.ts): the two figures side by side with the rule's
// citation, the SAME template whatever the numbers say, so a shorter-than-recorded notice is never
// coloured or reworded into a verdict. The backend's full sentence is on the meeting's own page,
// verbatim, behind an info glyph. How the count is made sits behind "How this works".
//
// ══ "WRITE MINUTES" ON A PAST MEETING (CBG-UX8-WRITE; frame m1) ═══════════════════════════════════
//
// A meeting that has started, wasn't called off and has no minutes opened (`awaitsMinutes`) says
// "Write minutes" to a holder of the draft door (`board_record:write`): its own link to the meeting's
// Minutes tab, where "Draft the minutes" is, beside the row's link and never inside it — at the
// row's right from `sm`, under the row's lines on a phone. It names no date and no duty: the Form 990
// clock is the "Writing things up" panel's, in its own words.

import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgBoardMeetingOut } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  Skeleton,
  saasButtonVariants,
} from '@givmo/design-system';
import { CalendarDays } from 'lucide-react';
import { Link } from '../../board-routing';
import { PageShell } from '../../components/page-shell';
import { GroupLabel, HowThisWorks, StatusWord } from '../../components/fine-print';
import { CarryForwardPanel, ContemporaneityListPanel } from './carry-forward-panel';
import { TermsEndingPanel } from './terms-ending-panel';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { MeetingFormDialog } from './meeting-form-dialog';
import {
  awaitsMinutes,
  extractMeetings,
  isUpcoming,
  meetingWhen,
  meetingCommitteeName,
  noticeFact,
  sortMeetings,
} from './meetings-decode';
import { useBoardOperations } from '../../board-runtime';
import { hasScheduleDraft } from '../../calendar/calendar-connect';
import { useBoardCalendarSettings } from '../../calendar/calendar-queries';
import { calendarWords } from '../../calendar/calendar-words';

const selectMeetings = (data: unknown): OrgBoardMeetingOut[] => sortMeetings(extractMeetings(data));

/** The fine print behind the page's one line. Mechanics only — Givmo counts; it does not judge. */
const HOW_THE_COUNT_WORKS = [
  'Each meeting shows how many days ahead it was scheduled, beside the notice period your board recorded under Governance → Meeting rules.',
  'Days are counted in the meeting’s own time zone, from the day it was scheduled to the day it’s held.',
  'Givmo counts the days. It doesn’t judge whether notice was enough.',
] as const;

/**
 * The same fine print where meetings can be kept in a connected calendar (CBG-CAL1): the plan's call
 * 1, verbatim (RESOLVED 2026-10-01 15:35 PDT; the `meetings-how-count` frame), before the last line.
 * DARK until served: only once the connected channel's settings read answers (it is the route miss
 * while `BOARD_CALENDAR_INVITATIONS_ENABLED` is off), so no one reads about a calendar they can't have.
 */
const HOW_THE_COUNT_WORKS_WITH_CALENDAR = [
  ...HOW_THE_COUNT_WORKS.slice(0, -1),
  calendarWords.howCountInCalendar,
  ...HOW_THE_COUNT_WORKS.slice(-1),
] as const;

export function MeetingsPage() {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { orgRef, isReady } = useBoardHost();
  const canSchedule = useBoardPermission('board_meeting:write');
  // Drafting a meeting's minutes is the draft door: "Write minutes" is offered only to its holder.
  const canDraftMinutes = useBoardPermission('board_record:write');
  const calendarServed = useBoardCalendarSettings(orgRef, isReady).data != null;
  const [creating, setCreating] = useState(false);
  // Back from Google in the same tab (no pop-up): the schedule dialog reopens on the fields it kept.
  useEffect(() => {
    if (canSchedule && orgRef !== null && hasScheduleDraft(orgRef)) setCreating(true);
  }, [canSchedule, orgRef]);

  const meetingsOptions = operations.officerListBoardMeetingsOptions({
    path: { org_ref: orgRef ?? '' },
  });
  const meetingsQuery = useQuery({
    ...meetingsOptions,
    enabled: isReady && orgRef !== null,
    select: selectMeetings,
  });

  const meetings = meetingsQuery.data ?? [];
  const { upcoming, past } = useMemo(
    () => ({
      upcoming: meetings.filter((meeting) => isUpcoming(meeting)),
      past: meetings.filter((meeting) => !isUpcoming(meeting)),
    }),
    [meetings],
  );

  return (
    <PageShell
      title="Meetings"
      description={
        <>
          Your board’s upcoming and past meetings.{' '}
          <HowThisWorks
            testId="meetings-how"
            lines={calendarServed ? HOW_THE_COUNT_WORKS_WITH_CALENDAR : HOW_THE_COUNT_WORKS}
          />
        </>
      }
      actions={
        // Scheduling is `board_meeting:write`: a reader without it is offered nothing to press.
        canSchedule ? (
          <Button
            appearance="saas"
            tone="primary"
            disabled={!isReady}
            onClick={() => setCreating(true)}
            testId="meetings-schedule"
          >
            Schedule a meeting
          </Button>
        ) : undefined
      }
      testId="page-meetings"
    >
      {meetingsQuery.isPending && isReady ? (
        <Skeleton className="h-90 w-full rounded-lg" testId="meetings-loading" />
      ) : null}

      {meetingsQuery.isError ? (
        <Card testId="meetings-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this board’s meetings.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void meetingsQuery.refetch()}
              testId="meetings-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {meetingsQuery.isSuccess && meetings.length === 0 ? (
        <EmptyState
          testId="meetings-empty"
          icon={<Icon icon={CalendarDays} size={20} aria-hidden />}
          title="No meetings scheduled"
          description={
            canSchedule
              ? 'Schedule one and we’ll show the notice figures against the period your organization recorded.'
              : undefined
          }
          action={
            canSchedule ? (
              <Button
                appearance="saas"
                tone="primary"
                onClick={() => setCreating(true)}
                testId="meetings-empty-schedule"
              >
                Schedule a meeting
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {/* The panels render nothing when there is nothing to say, so they cost an officer no
          attention on an ordinary day and lead the page on the day they matter. A new member's
          officer steps (CBG-YR4-START's panel, g3) are on the officer's to-do list, beside every
          Board page (CBG-UX7-WEB-OFF). */}
      {orgRef !== null ? <CarryForwardPanel orgRef={orgRef} /> : null}
      {orgRef !== null ? <ContemporaneityListPanel orgRef={orgRef} /> : null}
      {/* CBG-YR3-TERMS (frame t7): terms ended or ending, and the next annual meeting, if any. */}
      {orgRef !== null ? (
        <TermsEndingPanel
          orgRef={orgRef}
          annualMeeting={
            // CBG-FIX79's sibling: a meeting called off is nobody's annual meeting.
            upcoming.find(
              (meeting) => meeting.meeting_type === 'annual' && meeting.status === 'scheduled',
            ) ?? null
          }
        />
      ) : null}

      {upcoming.length > 0 ? (
        <MeetingGroup
          title="Upcoming"
          meetings={upcoming}
          canDraftMinutes={canDraftMinutes}
          testId="meetings-upcoming"
        />
      ) : null}
      {past.length > 0 ? (
        <MeetingGroup
          title="Past"
          meetings={past}
          canDraftMinutes={canDraftMinutes}
          testId="meetings-past"
        />
      ) : null}

      {creating && orgRef !== null ? (
        <MeetingFormDialog
          orgRef={orgRef}
          meeting={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: meetingsOptions.queryKey });
          }}
        />
      ) : null}
    </PageShell>
  );
}

function MeetingGroup({
  title,
  meetings,
  canDraftMinutes,
  testId,
}: {
  title: string;
  meetings: readonly OrgBoardMeetingOut[];
  canDraftMinutes: boolean;
  testId: string;
}) {
  return (
    <section className="flex flex-col gap-10" data-testid={testId}>
      <GroupLabel>{title}</GroupLabel>
      <Card className="overflow-hidden">
        <ul className="divide-y divide-border-subtle">
          {meetings.map((meeting) => (
            <MeetingRow
              key={meeting.id}
              meeting={meeting}
              writeMinutes={canDraftMinutes && awaitsMinutes(meeting)}
            />
          ))}
        </ul>
      </Card>
    </section>
  );
}

/** The row's link; with "Write minutes" beside it, it keeps that link's room at its right from `sm`. */
const ROW_LINK =
  'group grid grid-cols-1 items-baseline gap-x-16 gap-y-4 px-20 outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover sm:grid-cols-[minmax(0,1fr)_auto]';

function MeetingRow({
  meeting,
  writeMinutes,
}: {
  meeting: OrgBoardMeetingOut;
  writeMinutes: boolean;
}) {
  const cancelled = meeting.status === 'cancelled';
  // CBG-YR6-MEET (frame c4's list): a committee's meeting leads its fact with the committee, and its
  // notice is the committee's own.
  const committee = meetingCommitteeName(meeting);
  const titleId = useId();
  return (
    <li
      className={writeMinutes ? 'relative' : undefined}
      data-testid={`meetings-card-${meeting.id}`}
    >
      {/* One grid, two readings: at phone width the date follows the title; from `sm` it sits at
          the row's right and the fact takes the second line. */}
      <Link
        to="/meetings/$meetingId"
        params={{ meetingId: meeting.id }}
        className={
          writeMinutes ? `${ROW_LINK} pb-8 pt-14 sm:pb-14 sm:pr-[148px]` : `${ROW_LINK} py-14`
        }
        data-testid={`meetings-link-${meeting.id}`}
      >
        <span
          id={titleId}
          className="min-w-0 font-sans text-s15 font-w600 tracking-givmo text-text-strong transition-colors group-hover:text-brand-700"
        >
          {meeting.title}
        </span>
        <span className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary tabular-nums sm:text-right">
          {meetingWhen(meeting)}
        </span>
        <span
          className="min-w-0 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid={`meetings-notice-${meeting.id}`}
        >
          {committee !== null ? (
            <span
              className="font-w600 text-text-secondary"
              data-testid={`meetings-committee-${meeting.id}`}
            >
              {committee} ·{' '}
            </span>
          ) : null}
          {cancelled ? (
            <>
              <StatusWord>Cancelled</StatusWord>
              {meeting.cancellation_reason ? ` · ${meeting.cancellation_reason}` : null}
            </>
          ) : (
            noticeFact(meeting.notice, meeting.meeting_type, committee)
          )}
        </span>
      </Link>
      {writeMinutes ? (
        // Its own link, beside the row's and never inside it: under the row's lines on a phone, at
        // its right from `sm`, where the row keeps the room for it. The meeting's title describes it,
        // so a screen reader moving between links hears which meeting's minutes it opens.
        <div className="px-20 pb-14 sm:absolute sm:right-20 sm:top-1/2 sm:-translate-y-1/2 sm:p-0">
          <Link
            to="/meetings/$meetingId/minutes"
            params={{ meetingId: meeting.id }}
            aria-describedby={titleId}
            className={saasButtonVariants({
              size: 'sm',
              tone: 'secondary',
              className: 'no-underline',
            })}
            data-testid={`meetings-write-minutes-${meeting.id}`}
          >
            {/* Drawn m1: the plan's words (CBG-UX8 item 5), approved in the wayfinding copy round. */}
            Write minutes
          </Link>
        </div>
      ) : null}
    </li>
  );
}
